import React, { useState, useEffect } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import VoteButtonsMini from "components/card/VoteButtonsMini";
import useAuth from "hooks/auth/useAuth";
import usePredictorVotes from "hooks/predictors/usePredictorVotes";
import { calculatePoints } from "libs/utils";
import useLikes from "hooks/predictions/useLikes";
import CommentSection from "components/social/CommentSection";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { IconButton, Dialog, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { collection, getDocs } from "firebase/firestore";
import { db } from "config/firebase";
import useDarkMode from "hooks/useDarkMode";

const PredictorCardInfo = ({ item, order = {} }) => {
  const router = useRouter();
  const { handleVote } = usePredictorVotes();
  const { currentUser } = useAuth();
  const [currentCard, setCurrentCard] = useState(item);
  const data = currentCard.votes
    ? calculatePoints(currentCard.votes)
    : {
        agreeCount: 0,
        disagreeCount: 0,
        points: 0,
      };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { theme: appTheme } = useDarkMode();
  const isLight = appTheme === "light";

  const { likeCount, toggleLike, checkIfLiked } = useLikes(
    `predictors/${item.id}`,
  );
  const isLiked = checkIfLiked(currentUser?.uid);
  const [openComments, setOpenComments] = useState(false);
  const [commentCount, setCommentCount] = useState(0);

  useEffect(() => {
    if (!item.id) return;
    const commentsRef = collection(db, "predictors", item.id, "comments");
    // Use one-time fetch for list view
    getDocs(commentsRef).then((snapshot) => {
      setCommentCount(snapshot.size);
    });
  }, [item.id]);

  const firstName = item?._highlightResult?.firstName?.value ? (
    <div
      className="highlighted"
      dangerouslySetInnerHTML={{
        __html: item?._highlightResult?.firstName?.value || "",
      }}
    />
  ) : (
    currentCard.firstName
  );

  const lastName = item?._highlightResult?.lastName?.value ? (
    <div
      className="highlighted"
      dangerouslySetInnerHTML={{
        __html: item?._highlightResult?.lastName?.value || "",
      }}
    />
  ) : (
    currentCard.lastName
  );

  return (
    <div className="flex flex-col gap-[20px] p-[20px] md:p-[24px] rounded-[24px] bg-[#0c0a1f]/85 light:bg-slate-100/95 backdrop-blur-xl shadow-[0_0_30px_rgba(102,133,255,0.03)] transition-all duration-500 w-full group">
      <div className="flex flex-col md:flex-row gap-[16px] md:gap-[24px] items-start">
        {/* Profile Image & Quick Stats Area */}
        <div className="flex flex-col gap-[12px] shrink-0 mx-auto md:mx-0">
          <div className="w-[140px] h-[140px] md:w-[160px] md:h-[160px] relative rounded-[20px] overflow-hidden ring-1 ring-cyan-500/30 shadow-[0_0_15px_rgba(102,133,255,0.1)]">
            <Image
              src={currentCard?.image || "/default_video.webp"}
              alt="Predictor"
              layout="fill"
              objectFit="cover"
              priority
              className="bg-no-repeat transition-transform duration-700 group-hover:scale-110"
            />
            {/* Overlay Gradient for Image */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          </div>

          {/* Quick Voting Counters */}
          <div className="flex justify-between items-center backdrop-blur-md rounded-xl p-2 border border-white/5">
            <div className="flex flex-col items-center">
              <span className="text-[10px] text-red-400  uppercase tracking-tighter opacity-70">
                Դեմ
              </span>
              <span className="text-white text-[14px] font-bold">
                {data.disagreeCount}
              </span>
            </div>
            <VoteButtonsMini
              currentUser={currentUser}
              state={currentCard.votes?.[currentUser?.uid] || ""}
              onChange={(state) => {
                handleVote(currentCard.id, currentUser?.uid, state);
                setCurrentCard((prev) => ({
                  ...prev,
                  votes: {
                    ...prev.votes,
                    [currentUser.uid]: state,
                  },
                }));
              }}
            />
            <div className="flex flex-col items-center">
              <span className="text-[10px] text-emerald-400  uppercase tracking-tighter opacity-70">
                Կողմ
              </span>
              <span className="text-white text-[14px] font-bold">
                {data.agreeCount}
              </span>
            </div>
          </div>
        </div>

        {/* Info Content Area */}
        <div className="flex flex-col gap-[8px] w-full mt-2 md:mt-0">
          <div className="flex justify-between items-start">
            <h3 className="text-cyan-50 text-[18px] md:text-[22px] font-black tracking-tight flex items-center gap-2 group-hover:text-cyan-300 transition-colors uppercase leading-none">
              {firstName} {lastName}
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                width="16"
                height="16"
                className="text-cyan-400"
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            </h3>
          </div>

          {/* Detailed Points Badges */}
          <div className="flex items-center gap-[10px] mt-1">
            <div
              className={`flex items-center gap-[0px] ${order.orderBy === "collectedPoints" ? "flex-row" : "flex-row-reverse"}`}
            >
              <div
                className={`rounded-lg px-2.5 py-1 text-[12px] font-black transition-all ${order.orderBy === "collectedPoints" ? "bg-emerald-500 text-white shadow-[0_0_10px_rgba(16,185,129,0.3)]" : "bg-white/5 text-slate-400 border border-white/10"}`}
              >
                Ճիշտ {currentCard.correct || 0}
              </div>
              <div
                className={`rounded-lg px-2.5 py-1 text-[12px] font-black transition-all ${order.orderBy === "lostPoints" ? "bg-red-500 text-white shadow-[0_0_10px_rgba(239,68,68,0.3)]" : "bg-white/5 text-slate-400 border border-white/10"}`}
              >
                Սխալ {currentCard.wrong || 0}
              </div>
            </div>
          </div>

          <Dialog
            fullWidth
            maxWidth="sm"
            open={openComments}
            onClose={() => setOpenComments(false)}
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                backgroundColor: isLight
                  ? "rgba(255, 255, 255, 0.95)"
                  : "#030014",
                backgroundImage: isLight
                  ? "none"
                  : "linear-gradient(to bottom, rgba(102, 133, 255, 0.05), transparent)",
                border: isLight
                  ? "1px solid rgba(0, 0, 0, 0.1)"
                  : "1px solid rgba(102, 133, 255, 0.2)",
                borderRadius: isMobile ? 0 : "24px",
                boxShadow: "0 0 40px rgba(0,0,0,0.2)",
                backdropFilter: "blur(20px)",
              },
            }}
          >
            <div
              className={`flex justify-between items-center p-4 border-b border-cyan-500/10 bg-[#030014] light:bg-white light:border-slate-200`}
            >
              <h3 className="text-cyan-50 font-bold tracking-wider light:text-slate-900">
                Մեկնաբանություններ
              </h3>
              <IconButton
                onClick={() => setOpenComments(false)}
                sx={{ color: "rgba(102, 133, 255, 0.7)" }}
              >
                <CloseIcon />
              </IconButton>
            </div>
            <div className="overflow-y-auto bg-transparent">
              <CommentSection
                docPath={`predictors/${item.id}`}
                isInModal={true}
              />
            </div>
          </Dialog>

          <p className="text-slate-300 text-[13px] md:text-[15px] line-clamp-4 md:line-clamp-5 leading-relaxed mt-2 opacity-80 font-medium">
            {currentCard.description || "Կենսագրությունը բացակայում է։"}
          </p>
        </div>
      </div>

      <div className="flex flex-row items-center gap-6 mt-4 pt-4 border-t border-cyan-500/10 px-2">
        <button
          onClick={() => toggleLike(currentUser?.uid)}
          className={`flex items-center gap-2 text-sm font-bold transition-all duration-300 ${isLiked ? (isLight ? "text-cyan-600 shadow-sm scale-110" : "text-cyan-400 drop-shadow-[0_0_8px_rgba(102,133,255,0.5)] scale-110") : isLight ? "text-slate-500 hover:text-cyan-600" : "text-slate-400 hover:text-cyan-400"}`}
        >
          {isLiked ? (
            <ThumbUpIcon sx={{ fontSize: 20 }} />
          ) : (
            <ThumbUpOutlinedIcon sx={{ fontSize: 20 }} />
          )}
          <span>{likeCount || 0}</span>
        </button>

        <button
          onClick={() => setOpenComments(true)}
          className={`flex items-center gap-2 text-sm font-bold transition-all duration-300 ${openComments ? (isLight ? "text-cyan-600" : "text-cyan-400") : isLight ? "text-slate-500 hover:text-cyan-600" : "text-slate-400 hover:text-cyan-400"}`}
        >
          <ChatBubbleOutlineIcon sx={{ fontSize: 20 }} />
          <span>{commentCount || 0}</span>
        </button>
      </div>

      {/* Action Buttons Section */}
      <div className="grid grid-cols-3 gap-[10px] w-full mt-2">
        <button
          className="rounded-xl h-[44px] md:h-[50px] bg-white/5 hover:bg-white/10 border border-white/10 text-gray-800 dark:text-white text-[12px] md:text-[13px] font-bold transition-all duration-300 flex items-center justify-center gap-2"
          onClick={() => router.push(`/predictors/${currentCard.id}`)}
        >
          Պատմություն
        </button>
        <button
          className={`rounded-xl h-[44px] md:h-[50px] text-[12px] md:text-[13px] font-bold transition-all duration-300 flex items-center justify-center gap-2 ${currentCard.predictionsCount > 0 ? "bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 text-cyan-50" : "bg-white/5 border-white/5 text-slate-500 cursor-not-allowed opacity-50"}`}
          onClick={() =>
            currentCard.predictionsCount > 0 &&
            router.push({
              pathname: "/predictions",
              query: { predictor: currentCard.id },
            })
          }
        >
          Կանխատեսում ({currentCard.predictionsCount})
        </button>
        <button
          className={`rounded-xl h-[44px] md:h-[50px] text-[12px] md:text-[13px] font-bold transition-all duration-300 flex items-center justify-center gap-2 ${currentCard.promisesCount > 0 ? "bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-50" : "bg-white/5 border-white/5 text-slate-500 cursor-not-allowed opacity-50"}`}
          onClick={() =>
            currentCard.promisesCount > 0 &&
            router.push({
              pathname: "/promises",
              query: { predictor: currentCard.id },
            })
          }
        >
          Խոստումներ ({currentCard.promisesCount})
        </button>
      </div>
    </div>
  );
};

export default PredictorCardInfo;
